import { useMemo, useState } from "react";
import { api, ApiFailure } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface Round {
  e: string;
  uk: string;
  d: string;
  actual?: number;
  guess?: number;
  points?: number;
  off?: string;
}
interface View {
  rounds: (Round | null)[];
  answered: number;
  total: number;
  points: number;
  maxPoints: number;
  sliderMin: number;
  sliderMax: number;
}
interface GuessExtra {
  round: number;
  actual: number;
  points: number;
  off: string;
}

const COPY = { bad_number: "Постав повзунок" };
const HUMAN_M = 1.7;
const STAGE = 150; // px

function fmtMeters(m: number): string {
  if (m < 0.01) return `${Math.round(m * 1000)} мм`;
  if (m < 1) return `${Math.round(m * 100)} см`;
  if (m < 1000) return m < 10 ? `${m.toFixed(1)} м` : `${Math.round(m)} м`;
  return `${(m / 1000).toFixed(1)} км`;
}

/** Slider 0..1000 ↔ metres on a log scale. */
function sliderToM(v: number, min: number, max: number): number {
  return min * (max / min) ** (v / 1000);
}
/** Object + human drawn to relative scale, both kept on screen. */
function ScaleStage({ objM, emoji, revealed }: { objM: number; emoji: string; revealed: boolean }) {
  const big = Math.max(objM, HUMAN_M);
  const objIsBig = objM >= HUMAN_M;
  const smallFrac = Math.max(0.06, Math.min(objM, HUMAN_M) / big);
  const objH = objIsBig ? STAGE : STAGE * smallFrac;
  const humanH = objIsBig ? STAGE * smallFrac : STAGE;
  return (
    <div className="flex items-end justify-center gap-8 border-b-2 border-[rgba(128,128,128,0.4)] px-4" style={{ height: STAGE + 6 }}>
      <div className="flex flex-col items-center">
        <span style={{ fontSize: humanH, lineHeight: 1 }}>🧍</span>
        <span className="mt-0.5 text-[10px] text-tghint">1.7 м</span>
      </div>
      <div className="flex flex-col items-center">
        <span style={{ fontSize: objH, lineHeight: 1, transition: "font-size .25s ease-out" }}>{emoji}</span>
        <span className={`mt-0.5 text-[10px] font-semibold ${revealed ? "text-ok" : "text-tgbutton"}`}>{fmtMeters(objM)}</span>
      </div>
    </div>
  );
}

export function Naoko({ date }: { date?: string | undefined }) {
  const g = useGame<View>("naoko", COPY, date);
  const [slider, setSlider] = useState(500);
  const [reveal, setReveal] = useState<GuessExtra | null>(null);
  const [busy, setBusy] = useState(false);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const v = state?.view;
  const cur = v ? (v.rounds[v.answered] as Round | null) : null;
  const guessM = useMemo(() => (v ? sliderToM(slider, v.sliderMin, v.sliderMax) : 0), [slider, v]);

  // Direct api.guess (not g.run) so the sheet doesn't auto-pop over the reveal
  // panel on the final round — nextRound opens it on the "Підсумок →" tap.
  const lock = async () => {
    if (!v || !cur || busy || reveal) return;
    haptic.tap();
    setBusy(true);
    try {
      const next = await api.guess<View>("naoko", String(guessM), date);
      g.apply(next);
      const extra = next.extra as GuessExtra | undefined;
      if (extra) {
        setReveal(extra);
        if (extra.points >= 60) haptic.success();
        else haptic.error();
      }
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      if (code === "game_over") void g.load();
      else g.showToast(COPY[code as keyof typeof COPY] ?? `Щось зламалось (${code})`, 2000);
    } finally {
      setBusy(false);
    }
  };

  const nextRound = () => {
    setReveal(null);
    setSlider(500);
    if (finished) g.setSheetOpen(true);
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const view = s.view;
  // During a reveal, freeze on the just-answered round.
  const shownRound = reveal ? (view.rounds[reveal.round] as Round) : cur;
  const stageM = reveal ? reveal.actual : guessM;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="НА ОКО" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex flex-1 flex-col px-4 pb-3">
        <div className="mt-1 flex items-center justify-center gap-1.5">
          {Array.from({ length: view.total }, (_, i) => {
            const r = view.rounds[i];
            const done = r?.points !== undefined;
            return (
              <span
                key={i}
                className={`h-2.5 w-2.5 rounded-full ${
                  done ? ((r?.points ?? 0) >= 60 ? "bg-ok" : (r?.points ?? 0) > 0 ? "bg-warm" : "bg-cold") : i === view.answered ? "bg-tgbutton" : "bg-[rgba(128,128,128,0.25)]"
                }`}
              />
            );
          })}
        </div>
        <div className="mt-1 text-center text-xs text-tghint">
          балів: <b className="text-tgtext">{view.points}</b> / {view.maxPoints} · раунд {Math.min(view.answered + 1, view.total)}/{view.total}
        </div>

        {shownRound && (
          <div className="flex flex-1 flex-col justify-center gap-3">
            <div className="text-center">
              <div className="text-lg font-bold">{shownRound.e} {shownRound.uk}</div>
              <div className="text-xs text-tghint">{shownRound.d} — ?</div>
            </div>

            <ScaleStage objM={stageM} emoji={shownRound.e} revealed={Boolean(reveal)} />

            {!reveal ? (
              <>
                <div className="text-center">
                  <span className="rounded-xl bg-tgsection px-5 py-1.5 text-2xl font-extrabold tabular-nums">{fmtMeters(guessM)}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1000}
                  value={slider}
                  onChange={(e) => setSlider(Number(e.target.value))}
                  className="h-6 w-full"
                  style={{ accentColor: "var(--tg-theme-button-color, #2ea6ff)" }}
                  aria-label="розмір"
                />
                <div className="flex justify-between px-1 text-[10px] text-tghint">
                  <span>{fmtMeters(view.sliderMin)}</span>
                  <span>метр</span>
                  <span>{fmtMeters(view.sliderMax)}</span>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void lock()}
                  className="mx-auto mt-1 w-full max-w-[420px] rounded-xl bg-tgbutton py-3.5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-40"
                >
                  {busy ? "…" : "Зафіксувати 📏"}
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="text-center">
                  <div className="text-sm text-tghint">насправді</div>
                  <div className="text-2xl font-extrabold text-ok">{fmtMeters(reveal.actual)}</div>
                  <div className="mt-1 text-sm">
                    твоя версія: <b>{fmtMeters((view.rounds[reveal.round] as Round).guess ?? 0)}</b> · {reveal.off}
                  </div>
                  <div className={`mt-1 text-lg font-bold ${reveal.points >= 60 ? "text-ok" : reveal.points > 0 ? "text-warm" : "text-cold"}`}>
                    +{reveal.points} балів
                  </div>
                </div>
                <button
                  type="button"
                  onClick={nextRound}
                  className="mt-1 w-full max-w-[420px] rounded-xl bg-tgbutton py-3.5 text-[15px] font-bold text-tgbuttontext active:brightness-90"
                >
                  {finished ? "Підсумок →" : "Далі →"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={view.points >= 400 ? "Око-міра!" : view.points >= 250 ? "Незле!" : "Буває"}
          subtitle={`📏 ${view.points}/${view.maxPoints} · стрік ${s.me.streak} 🔥`}
          scoreLabel="avg бали"
          formatScore={(n) => (n === null ? "–" : `${view.maxPoints - n}/${view.maxPoints}`)}
          onDismiss={() => g.setSheetOpen(false)}
        >
          <ul className="mt-3 space-y-1.5">
            {view.rounds.map((r, i) =>
              r ? (
                <li key={i} className="flex items-center justify-between rounded-xl bg-tgsection px-3 py-2 text-sm">
                  <span>{r.e} {r.uk} <span className="text-tghint">({fmtMeters(r.actual ?? 0)})</span></span>
                  <span className={`font-semibold ${(r.points ?? 0) >= 60 ? "text-ok" : (r.points ?? 0) > 0 ? "text-warm" : "text-cold"}`}>+{r.points ?? 0}</span>
                </li>
              ) : null,
            )}
          </ul>
        </ResultSheet>
      )}
    </Shell>
  );
}
